(ns app.front.ui.anchored-popup
  (:require [core.front.portal :as portal]
            [core.util.util :as util]
            [reagent.core :as r]))

(defn position
  "Position a panel below its anchor, clamped inside the viewport with an eight-pixel margin."
  [{:keys [left bottom]} {panel-width :width panel-height :height}
   {viewport-width :width viewport-height :height}]
  {:left (util/clamp left 8 (max 8 (- viewport-width panel-width 8)))
   :top  (util/clamp (+ bottom 4) 8 (max 8 (- viewport-height panel-height 8)))})

(defn popup
  "Nonmodal popup rendered in a portal. Mount to open, unmount to close.

   Focus starts on the panel and returns to the anchor on close unless another control has focus.
   Children are arbitrary content.

   Props:
   `:anchor`     DOM element, the trigger below which the popup should be positioned
   `:on-close`   function, requests dismissal on Escape, outside pointer interaction or focus leaving popup/trigger
   `:aria-label` string, the accessible name of the nonmodal dialog
   `:id`         optional string, the ID of the popup div
   `:class`      optional string, the CSS classes for the popup div
   `:style`      optional map, the CSS style override for the popup div
   `:data-cy`    optional string, the test selector for the popup div"
  [{:keys [anchor on-close]} & _]
  (let [*anchor-ref       (r/atom anchor)
        *popup-ref        (r/atom nil)
        *popup-pos        (r/atom nil)
        *on-close         (r/atom on-close)
        *focus-pending?   (r/atom true)
        *resize-observer  (r/atom nil)
        *content-observer (r/atom nil)
        inside?           (fn [target]
                            (or (some-> @*popup-ref (.contains target))
                                (some-> @*anchor-ref (.contains target))))
        close!            #(when-let [on-close @*on-close]
                             (on-close))
        update-pos        (fn []
                            (when (and @*anchor-ref @*popup-ref)
                              (let [panel     @*popup-ref
                                    bounds    (.getBoundingClientRect @*anchor-ref)
                                    height    (max (.-offsetHeight panel) (.-scrollHeight panel))
                                    available (- (.-innerHeight js/window) 16)
                                    new-pos   (assoc
                                                (position
                                                  {:left (.-left bounds) :bottom (.-bottom bounds)}
                                                  {:width (.-offsetWidth panel) :height height}
                                                  {:width (.-innerWidth js/window) :height (.-innerHeight js/window)})
                                                :overflow (if (> height available) "auto" "visible"))]
                                (when (not= @*popup-pos new-pos)
                                  (reset! *popup-pos new-pos)))))
        dismiss!          (fn [event]
                            (when-not (inside? (.-target event))
                              (close!)))]
    (r/create-class
      {:component-did-mount
       (fn [_]
         (let [resize-observer  (js/ResizeObserver. update-pos)
               ;; Out-of-flow calendars can change scrollHeight without resizing the panel's border box.
               content-observer (js/MutationObserver. update-pos)]
           (reset! *resize-observer resize-observer)
           (reset! *content-observer content-observer)
           (.observe resize-observer @*popup-ref)
           (.observe resize-observer @*anchor-ref)
           (.observe content-observer @*popup-ref #js {:childList true :subtree true :characterData true})
           (.addEventListener js/window "resize" update-pos)
           (.addEventListener js/window "scroll" update-pos true)
           (.addEventListener js/document "pointerdown" dismiss!)
           (update-pos)))
       :component-did-update
       (fn [component _]
         (let [{:keys [anchor on-close]} (r/props component)]
           (when (not= @*anchor-ref anchor)
             (reset! *anchor-ref anchor)
             (some-> @*resize-observer .disconnect)
             (.observe @*resize-observer @*popup-ref)
             (.observe @*resize-observer anchor))
           (reset! *on-close on-close)
           (update-pos)
           ;; The first position update has now rendered the visible panel.
           (when @*focus-pending?
             (reset! *focus-pending? false)
             (.focus @*popup-ref))))
       :component-will-unmount
       (fn [_]
         (some-> @*resize-observer .disconnect)
         (some-> @*content-observer .disconnect)
         (.removeEventListener js/window "resize" update-pos)
         (.removeEventListener js/window "scroll" update-pos true)
         (.removeEventListener js/document "pointerdown" dismiss!)
         (let [active (.-activeElement js/document)]
           ;; Disabling a focused form control during submission moves focus to body.
           (when (or (.contains @*popup-ref active) (identical? active js/document.body))
             (some-> @*anchor-ref .focus))))
       :reagent-render
       (fn [{:keys [id aria-label class style data-cy]} & children]
         (portal/render-in-portal
           (into [:div.bg-white.border.border-gray-300.shadow-lg.rounded.text-left
                  {:id               id
                   :role             "dialog"
                   :aria-label       aria-label
                   :tab-index        -1
                   :class            class
                   :data-cy          data-cy
                   :ref              #(reset! *popup-ref %)
                   ;; Entrance transforms can change an overflowing child's footprint
                   ;; after mutation/resize observers have already run.
                   :on-animation-end update-pos
                   :on-key-down      (fn [event]
                                       (when (= "Escape" (.-key event))
                                         (.stopPropagation event)
                                         (.preventDefault event)
                                         (close!)))
                   :on-blur          (fn [event]
                                       (when-let [target (.-relatedTarget event)]
                                         (when-not (inside? target)
                                           (close!))))
                   :style            (merge {:position   "fixed"
                                             :visibility (if @*popup-pos "visible" "hidden")
                                             :z-index    10000
                                             :max-width  "calc(100vw - 16px)"
                                             :max-height "calc(100dvh - 16px)"}
                                            style
                                            @*popup-pos)}]
                 children)))})))
